// stepper.component.ts
import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CompanyService, ApplicationData } from '../services/company.service';
import { Subject, takeUntil } from 'rxjs';

export interface Step {
  number: number;
  label: string;
  description: string;
  icon: string;
  active: boolean;
  inactiveIcon?: string;
  done: boolean;
}

@Component({
  selector: 'app-stepper',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './stepper.component.html',
  styleUrls: ['./stepper.component.scss'],
})
export class StepperComponent implements OnChanges, OnInit, OnDestroy {
  @Input() currentStep = 1;

  steps: Step[] = [
    {
      number: 1,
      label: 'STEPPER.STEPS.STEP_1.LABEL',
      description: 'STEPPER.STEPS.STEP_1.DESCRIPTION',
      icon: 'assets/icons/company-details.svg',
      inactiveIcon: 'assets/icons/company-details-inactive.svg',
      active: false,
      done: false,
    },
    {
      number: 2,
      label: 'STEPPER.STEPS.STEP_2.LABEL',
      description: 'STEPPER.STEPS.STEP_2.DESCRIPTION',
      icon: 'assets/icons/add-members.svg',
      inactiveIcon: 'assets/icons/add-members-inactive.svg',
      active: false,
      done: false,
    },
    {
      number: 3,
      label: 'STEPPER.STEPS.STEP_3.LABEL',
      description: 'STEPPER.STEPS.STEP_3.DESCRIPTION',
      icon: 'assets/icons/health-declaration.svg',
      inactiveIcon: 'assets/icons/health-declaration-inactive.svg',
      active: false,
      done: false,
    },
    {
      number: 4,
      label: 'STEPPER.STEPS.STEP_4.LABEL',
      description: 'STEPPER.STEPS.STEP_4.DESCRIPTION',
      icon: 'assets/icons/select-quote.svg',
      inactiveIcon: 'assets/icons/select-quote-inactive.svg',
      active: false,
      done: false,
    },
    {
      number: 5,
      label: 'STEPPER.STEPS.STEP_5.LABEL',
      description: 'STEPPER.STEPS.STEP_5.DESCRIPTION',
      icon: 'assets/icons/payment.svg',
      inactiveIcon: 'assets/icons/payment-inactive.svg',
      active: false,
      done: false,
    },
  ];

  selectedCompanyName: string = 'Not set';
  employeeCount: number = 0;
  dependentCount: number = 0;
  totalMembers: number = 0;
  totalMale: number = 0;
  totalFemale: number = 0;
  selectedQuote: string = 'Not selected';
  progress: number = 0;

  private destroy$ = new Subject<void>();

  constructor(
    private companyService: CompanyService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.updateSteps();
    this.subscribeToApplicationData();
    this.subscribeToLanguageChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentStep']) {
      this.updateSteps();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  subscribeToLanguageChanges(): void {
    this.translateService.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // Language changed, translations will be updated automatically via the translate pipe
      });
  }

  subscribeToApplicationData(): void {
    this.companyService.applicationData$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: ApplicationData) => {
        this.selectedCompanyName = data.companyName || 'Not set';
        this.employeeCount = data.employeeCount;
        this.dependentCount = data.dependentCount || 0;
        this.totalMembers = this.employeeCount + this.dependentCount;
        this.totalMale = data.maleCount || 0;
        this.totalFemale = data.femaleCount || 0;
        this.selectedQuote = data.selectedQuote || 'Not selected';
        this.progress = this.companyService.getProgress();
        console.log('Application Data Updated:', data);
        // Update step done status based on completed steps
        this.steps.forEach((step) => {
          step.done = data.completedSteps.includes(step.number);
        });
      });
  }

  updateSteps(): void {
    this.steps.forEach((step) => {
      step.active = step.number === this.currentStep;
    });

    console.log(
      'Updated steps:',
      this.steps,
      'Current Step:',
      this.currentStep
    );
  }
}
